import { get } from 'lodash-es';

import { Styles, StyleSheet } from '@react-pdf/renderer';

type ChakraStyle = {
  color?: string;
  backgroundColor?: string;
  fontSize?: string | number;
  fontWeight?: string | number;
  padding?: string | number;
  margin?: string | number;
  [key: string]: any;
};

// Convert rem to pixels (1rem = 16px by default)
function remToPixels(rem: string): number {
  const remValue = parseFloat(rem);
  return remValue * 16;
}

// Convert pixels to points (1pt = 1/72 inch, 1px = 1/96 inch)
function pixelsToPoints(pixels: number): number {
  return pixels * (72 / 96);
}

// Convert a value with unit to points
function convertToPoints(value: string | number): number {
  if (typeof value === 'number') {
    return pixelsToPoints(value);
  }

  if (value.endsWith('rem')) {
    return pixelsToPoints(remToPixels(value));
  }

  if (value.endsWith('px')) {
    return pixelsToPoints(parseFloat(value));
  }

  // If no unit is specified, assume pixels
  return pixelsToPoints(parseFloat(value));
}

export function convertChakraToPdfStyles<T extends Record<string, ChakraStyle>, S extends Styles>(theme: T, styles: S) {
  const convertedStyles: Record<keyof S, ChakraStyle> = {} as Record<keyof S, Styles>;

  Object.entries(styles).forEach(([key, style]) => {
    convertedStyles[key as keyof S] = {};

    Object.entries(style).forEach(([property, value]) => {
      if (typeof value === 'string') {
        // Handle color tokens
        if (property === 'color' || property === 'backgroundColor') {
          convertedStyles[key][property] = get(theme.colors, value) || value;
        }
        // Handle spacing tokens
        else if (property === 'padding' || property === 'margin') {
          const spacingValue = get(theme.space, value) || value;
          convertedStyles[key][property] = convertToPoints(spacingValue);
        }
        // Handle font size tokens
        else if (property === 'fontSize') {
          const fontSizeValue = get(theme.fontSizes, value) || value;
          convertedStyles[key][property] = convertToPoints(fontSizeValue);
        }
        // Handle font weight tokens
        else if (property === 'fontWeight') {
          convertedStyles[key][property] = get(theme.fontWeights, value) || value;
        }
        // Handle other properties that might have units
        else if (typeof value === 'string' && (value.endsWith('px') || value.endsWith('rem'))) {
          convertedStyles[key][property] = convertToPoints(value);
        }
        // Handle other properties
        else {
          convertedStyles[key][property] = value;
        }
      } else {
        convertedStyles[key][property] = value;
      }
    });
  });

  return StyleSheet.create(convertedStyles);
}
